import { useEffect, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type Shortcut = {
  keys: string;
  label: string;
};

type ShortcutGroup = {
  title: string;
  shortcuts: Shortcut[];
};

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "اختصارات عامة",
    shortcuts: [
      { keys: "shift+/", label: "عرض الاختصارات" },
      { keys: "mod+k", label: "فتح البحث" },
      { keys: "mod+m", label: "الوحدات" },
    ],
  },
  {
    title: "اختصارات إدخال البيانات",
    shortcuts: [
      { keys: "mod+s", label: "حفظ" },
      { keys: "mod+n", label: "سجل جديد" },
    ],
  },
];

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform);

const KEY_LABELS: Record<string, string> = {
  mod: isMac ? "⌘" : "Ctrl",
  shift: isMac ? "⇧" : "Shift",
  alt: isMac ? "⌥" : "Alt",
  option: isMac ? "⌥" : "Alt",
  ctrl: isMac ? "⌃" : "Ctrl",
  meta: isMac ? "⌘" : "Win",
  enter: isMac ? "⏎" : "Enter",
  esc: isMac ? "⎋" : "Esc",
  backspace: isMac ? "⌫" : "Backspace",
  delete: isMac ? "⌦" : "Delete",
  space: "Space",
  "/": "/",
};

const ORDER = isMac
  ? ["mod", "alt", "shift", "ctrl", "meta"]
  : ["ctrl", "alt", "shift", "meta", "mod"];

function formatShortcut(keys: string) {
  const parts = keys.toLowerCase().split("+");
  const ordered = [...parts].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
  return ordered.map((part) => KEY_LABELS[part] || part.toUpperCase());
}

function KeyBadge({ label }: { label: string }) {
  return (
    <span
      className={cn(
        "inline-flex min-w-[32px] items-center justify-center rounded-md border border-border/60 bg-muted px-2 py-1 text-xs font-semibold text-foreground"
      )}
    >
      {label}
    </span>
  );
}

function ShortcutKeys({ keys }: { keys: string }) {
  const display = useMemo(() => formatShortcut(keys), [keys]);
  return (
    <div className="flex flex-wrap items-center gap-1">
      {display.map((key, index) => (
        <KeyBadge key={`${keys}-${index}`} label={key} />
      ))}
    </div>
  );
}

interface Props {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function ShortcutsModal({ open: openProp, onOpenChange }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  useHotkeys("shift+/", () => setOpen(true), {
    preventDefault: true,
    enableOnFormTags: true,
    enableOnContentEditable: true,
  });
  useHotkeys("?", () => setOpen(true), {
    preventDefault: true,
    enableOnFormTags: true,
    enableOnContentEditable: true,
  });

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "?" || (event.code === "Slash" && event.shiftKey)) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setOpen]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>اختصارات لوحة المفاتيح</DialogTitle>
          <DialogDescription>عرض الاختصارات المتاحة في النظام.</DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title} className="space-y-2">
              <div className="text-sm font-semibold text-muted-foreground">{group.title}</div>
              <div className="overflow-hidden rounded-lg border">
                {group.shortcuts.map((shortcut, index) => (
                  <div
                    key={`${group.title}-${shortcut.keys}`}
                    className={cn(
                      "grid grid-cols-[160px_1fr] items-center gap-4 px-4 py-3 text-sm",
                      index % 2 === 0 ? "bg-background" : "bg-muted/40"
                    )}
                  >
                    <ShortcutKeys keys={shortcut.keys} />
                    <span>{shortcut.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
